import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
  Drawer,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";

import { useAuth } from "../context/AuthContext";
import CalendarSettings from "../components/settings/CalendarSettings";
import SocialMediaSettings from "../components/settings/SocialMediaSettings";
import ShopifySettings from "../components/settings/ShopifySettings";
import ClinikoSettings from "../components/settings/ClinikoSettings";
import api from "../api/client";
import { calendarDetails } from "../api/googlecalendar";
import { getSocialMediaStatus } from "../api/meta";

type BusinessMode = "service" | "shop" | "cliniko" | "real_estate" | "hybrid";
type CalendarProvider = "google" | "microsoft" | null;

type IntegrationKey = "meta" | "calendar" | "shopify" | "cliniko";

interface IntegrationDef {
  key: IntegrationKey;
  label: string;
  description: string;
  Icon: typeof TuneRoundedIcon;
  availableFor: BusinessMode[] | "all";
}

const INTEGRATIONS: IntegrationDef[] = [
  {
    key: "meta",
    label: "Meta (Facebook & Instagram)",
    description: "Connect your Facebook Page and Instagram account to enable AI-powered messaging and lead capture.",
    Icon: FacebookRoundedIcon,
    availableFor: "all",
  },
  {
    key: "calendar",
    label: "Calendar",
    description: "Sync Google Calendar or Microsoft Outlook to manage availability and bookings.",
    Icon: CalendarMonthRoundedIcon,
    availableFor: ["service", "cliniko", "real_estate", "hybrid"],
  },
  {
    key: "shopify",
    label: "Shopify",
    description: "Connect your Shopify store to enable AI-assisted order support and product enquiries.",
    Icon: StorefrontRoundedIcon,
    availableFor: ["shop"],
  },
  {
    key: "cliniko",
    label: "Cliniko",
    description: "Connect Cliniko to manage appointments, practitioners and clinical bookings via AI.",
    Icon: MedicalServicesRoundedIcon,
    availableFor: ["cliniko"],
  },
];

function isIntegrationKey(value: string | null): value is IntegrationKey {
  return (
    value === "meta" ||
    value === "calendar" ||
    value === "shopify" ||
    value === "cliniko"
  );
}

function IntegrationIcon({ Icon }: { Icon: typeof TuneRoundedIcon }) {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        backgroundColor: "#F8FAFC",
        border: "1px solid #E2E8F0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Icon sx={{ width: 28, height: 28, color: "#64748B" }} />
    </Box>
  );
}

export default function IntegrationsPage() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businessMode, setBusinessMode] = useState<BusinessMode | null>(null);
  const [loading, setLoading] = useState(true);

  // Social connection state (passed into SocialMediaSettings)
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [instagramConnected, setInstagramConnected] = useState(false);
  const [, setErrorMessage] = useState<string | null>(null);

  // Calendar state
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarProvider, setCalendarProvider] = useState<CalendarProvider>(null);

  // Drawer state
  const [activeDrawer, setActiveDrawer] = useState<IntegrationKey | null>(() => {
    const drawer = searchParams.get("drawer");
    return isIntegrationKey(drawer) ? drawer : null;
  });

  const calendarErrorMessage =
    activeDrawer === "calendar" ? searchParams.get("calendar_error") : null;

  const openDrawer = (drawer: IntegrationKey) => {
    setActiveDrawer(drawer);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("drawer");
      next.delete("calendar_error");
      return next;
    });
  };

  useEffect(() => {
    if (!currentUser) return;
    const fetch = async () => {
      try {
        const [bizRes, calRes, socialRes] = await Promise.allSettled([
          api.get("/getBusinessId"),
          calendarDetails(),
          getSocialMediaStatus(),
        ]);
        if (bizRes.status === "fulfilled") {
          setBusinessMode((bizRes.value.data?.business_mode as BusinessMode) || "service");
        } else {
          setBusinessMode("service");
        }
        if (calRes.status === "fulfilled") {
          setCalendarConnected(!!calRes.value.calendar_connected);
          setCalendarProvider((calRes.value.calendar_provider ?? null) as CalendarProvider);
        }
        if (socialRes.status === "fulfilled" && socialRes.value) {
          setFacebookConnected(!!socialRes.value.facebook_connected);
          setInstagramConnected(!!socialRes.value.instagram_connected);
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [currentUser]);

  const isAvailable = (integration: IntegrationDef): boolean => {
    if (integration.availableFor === "all") return true;
    if (!businessMode) return false;
    return (integration.availableFor as BusinessMode[]).includes(businessMode);
  };

  const isConnected = (key: IntegrationKey): boolean => {
    if (key === "meta") return facebookConnected || instagramConnected;
    if (key === "calendar") return calendarConnected;
    return false;
  };

  const drawerTitle = INTEGRATIONS.find((i) => i.key === activeDrawer)?.label ?? "";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress sx={{ color: "#C9734F" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
        <TuneRoundedIcon sx={{ color: "#C9734F", fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Integrations
        </Typography>
      </Box>

      <Typography sx={{ color: "#64748B", fontSize: "0.9rem", mb: 4, maxWidth: 560 }}>
        Connect your business tools to Setin. Your AI assistant will automatically use these connections to respond to customers and capture bookings.
      </Typography>

      {/* Integration cards */}
      <Grid container spacing={2.5}>
        {INTEGRATIONS.map((integration) => {
          const available = isAvailable(integration);
          const connected = isConnected(integration.key);
          const unavailableLabel =
            integration.availableFor !== "all"
              ? `Available for ${(integration.availableFor as string[]).join(", ")} accounts`
              : "";

          return (
            <Grid item xs={12} sm={6} key={integration.key}>
              <Tooltip
                title={!available ? unavailableLabel : ""}
                placement="top"
                arrow
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: available ? "#E2E8F0" : "#E2E8F0",
                    backgroundColor: "#fff",
                    opacity: available ? 1 : 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    transition: "box-shadow 0.15s ease",
                    ...(available && {
                      "&:hover": {
                        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      },
                    }),
                  }}
                >
                  {/* Card top */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <IntegrationIcon Icon={integration.Icon} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#0F172A" }}>
                          {integration.label}
                        </Typography>
                        {!available && (
                          <LockOutlinedIcon sx={{ fontSize: 14, color: "#94A3B8" }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: "0.82rem", color: "#64748B", lineHeight: 1.5 }}>
                        {integration.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: "#F1F5F9" }} />

                  {/* Card bottom */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    {/* Status */}
                    <Chip
                      size="small"
                      icon={
                        connected ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: "14px !important", color: "#22C55E !important" }} />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon sx={{ fontSize: "14px !important" }} />
                        )
                      }
                      label={connected ? "Connected" : "Not connected"}
                      sx={{
                        backgroundColor: connected ? "#F0FDF4" : "#F8FAFC",
                        color: connected ? "#16A34A" : "#64748B",
                        border: `1px solid ${connected ? "#BBF7D0" : "#E2E8F0"}`,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />

                    {/* CTA */}
                    <Button
                      size="small"
                      variant={connected ? "outlined" : "contained"}
                      disabled={!available}
                      onClick={() => available && openDrawer(integration.key)}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        px: 2,
                        ...(connected
                          ? {
                              borderColor: "#CBD5E1",
                              color: "#0F172A",
                              backgroundColor: "transparent",
                              "&:hover": { borderColor: "#94A3B8", backgroundColor: "#F8FAFC" },
                            }
                          : {
                              backgroundColor: "#C9734F",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#b8613f" },
                              boxShadow: "none",
                            }),
                      }}
                    >
                      {connected ? "Manage" : "Connect"}
                    </Button>
                  </Box>
                </Paper>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={activeDrawer !== null}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            backgroundColor: "#fff",
            borderLeft: "1px solid #E2E8F0",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid #F1F5F9" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>
            {drawerTitle}
          </Typography>
          <IconButton onClick={closeDrawer} size="small" sx={{ color: "#64748B" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
          {activeDrawer === "meta" && (
            <SocialMediaSettings
              facebookConnected={facebookConnected}
              instagramConnected={instagramConnected}
              setFacebookConnected={setFacebookConnected}
              setInstagramConnected={setInstagramConnected}
              setErrorMessage={setErrorMessage}
            />
          )}
          {activeDrawer === "calendar" && (
            <CalendarSettings
              calendarConnected={calendarConnected}
              calendarProvider={calendarProvider}
              setCalendarConnected={setCalendarConnected}
              setCalendarProvider={setCalendarProvider}
              initialError={calendarErrorMessage}
            />
          )}
          {activeDrawer === "shopify" && <ShopifySettings />}
          {activeDrawer === "cliniko" && <ClinikoSettings />}
        </Box>
      </Drawer>
    </Box>
  );
}
